/**
 * The team workspace (spec #44 §1) — `/marketing-agents`.
 *
 * A three-pane shell inside a Polaris `Page`: sidebar, main, right rail.
 * The rail column exists ONLY in the agent-opened state, which is a query
 * param (`?c={domain}`) rather than a route, so the board stays the route
 * and back/forward work.
 *
 * The main pane is one of four things, chosen by that param:
 *   nothing        → the board (three lanes)
 *   `needs-you`    → every open question in one list
 *   `team-now`     → every agent's entries interleaved
 *   a domain       → that agent's thread, with the effect rail beside it
 */

import { useEffect } from 'react';
import { Navigate, Route, useParams, useSearchParams } from 'react-router-dom';
import { Page, Text } from '@shopify/polaris';

import { AgentThread } from '../components/workspace/AgentThread';
import { BoardPane } from '../components/workspace/BoardPane';
import { ChannelSidebar } from '../components/workspace/ChannelSidebar';
import { EffectRail } from '../components/workspace/EffectRail';
import { QuestionsLane } from '../components/workspace/QuestionsLane';
import { TimelineEntryCard } from '../components/workspace/TimelineEntryCard';
import { WorkspaceEmpty } from '../components/workspace/WorkspaceEmpty';
import styles from '../components/workspace/workspace.module.css';
import { WorkspaceProvider } from '../contexts/WorkspaceContext';
import { useWorkspace } from '../hooks/useWorkspace';
import {
  NEEDS_YOU_CHANNEL,
  TEAM_NOW_CHANNEL,
} from '../types/workspace';
import AgentWorkspace from './AgentWorkspace';
import MarketingAgentDetail from './MarketingAgentDetail';
import MarketingAgents from './MarketingAgents';

/** `# team-now` — every agent interleaved, newest last. */
function TeamNowPane() {
  const { teamNowEntries, loadTeamNow, errors, loading } = useWorkspace();

  useEffect(() => {
    void loadTeamNow();
  }, [loadTeamNow]);

  return (
    <div data-testid="team-now-pane">
      <div className={styles.laneHead}>
        <span className={styles.laneTitle}>team-now</span>
        <span className={styles.subtle}>everyone, newest last</span>
      </div>
      <div className={styles.thread}>
        {errors.thread ? (
          <WorkspaceEmpty kind="lane-error" reason={errors.thread} />
        ) : teamNowEntries.length === 0 ? (
          loading.thread ? null : (
            <WorkspaceEmpty kind="no-cycle" />
          )
        ) : (
          [...teamNowEntries]
            .sort(
              (a, b) => Date.parse(a.occurred_at) - Date.parse(b.occurred_at)
            )
            .map((entry) => (
              <TimelineEntryCard key={entry.id} entry={entry} />
            ))
        )}
      </div>
    </div>
  );
}

function NeedsYouPane() {
  return (
    <div data-testid="needs-you-pane">
      <div className={styles.laneHead}>
        <span className={styles.laneTitle}>needs-you</span>
        <span className={styles.subtle}>
          everything waiting on you, in one list
        </span>
      </div>
      <QuestionsLane />
    </div>
  );
}

function WorkspaceShell() {
  const { openDomain, agents } = useWorkspace();
  const isFixedChannel =
    openDomain === NEEDS_YOU_CHANNEL || openDomain === TEAM_NOW_CHANNEL;
  const agentDomain = openDomain && !isFixedChannel ? openDomain : null;
  const knownAgent = agents.some((a) => a.domain === agentDomain);

  return (
    <Page fullWidth>
      <h1 className={styles.screenTitle}>Your marketing team</h1>
      <Text as="p" tone="subdued">
        Everything below is a record: what was asked, what ran, what it
        changed.
      </Text>
      <div
        className={agentDomain ? styles.shell : styles.shellNoRail}
        data-testid="workspace-shell"
      >
        <ChannelSidebar />
        <main className={styles.main}>
          {agentDomain ? (
            knownAgent || agents.length === 0 ? (
              <AgentThread domain={agentDomain} />
            ) : (
              <Text as="p" tone="subdued">
                {agentDomain} is not an agent in this workspace.
              </Text>
            )
          ) : openDomain === NEEDS_YOU_CHANNEL ? (
            <NeedsYouPane />
          ) : openDomain === TEAM_NOW_CHANNEL ? (
            <TeamNowPane />
          ) : (
            <BoardPane />
          )}
        </main>
        {agentDomain ? <EffectRail domain={agentDomain} /> : null}
      </div>
    </Page>
  );
}

export default function Workspace() {
  return (
    <WorkspaceProvider>
      <WorkspaceShell />
    </WorkspaceProvider>
  );
}

/** The persona variant, provider-wrapped the same way. */
export function AgentWorkspacePage() {
  return (
    <WorkspaceProvider>
      <AgentWorkspace />
    </WorkspaceProvider>
  );
}

/**
 * The old `/marketing-agents/:key` detail route, kept alive as a redirect
 * to its new home so no bookmark breaks (§Provisional 1).
 */
export function SpecialistRedirect() {
  const { key } = useParams();
  const [params] = useSearchParams();
  const qs = params.toString();
  return (
    <Navigate
      replace
      to={`/marketing-agents/s/${key ?? ''}${qs ? `?${qs}` : ''}`}
    />
  );
}

/**
 * Every route this surface owns, in one place so `App.tsx` mounts them as
 * a unit and the ordering (static before dynamic) cannot drift apart.
 */
export const WorkspaceRoutes = (
  <>
    <Route path="/marketing-agents" element={<Workspace />} />
    <Route path="/marketing-agents/specialists" element={<MarketingAgents />} />
    <Route
      path="/marketing-agents/s/:key"
      element={<MarketingAgentDetail />}
    />
    <Route
      path="/marketing-agents/a/:domain"
      element={<AgentWorkspacePage />}
    />
    <Route path="/marketing-agents/:key" element={<SpecialistRedirect />} />
  </>
);
